import React, { useCallback, useEffect, useRef } from "react";
import { Drawer } from "antd";

const SWIPE_CLOSE_MIN_PX = 48;
const SWIPE_DOMINANCE_RATIO = 1.15;
const SWIPE_AXIS_LOCK_PX = 10;

const isSwipeEnvironment = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(max-width: 991px)").matches ||
    window.matchMedia("(pointer: coarse)").matches ||
    navigator.maxTouchPoints > 0
  );
};

const getContentWrapper = (el) => el?.closest?.(".ant-drawer-content-wrapper") || null;

const resetWrapperTransform = (wrapper) => {
  if (!wrapper) return;
  wrapper.style.transform = "";
  wrapper.classList.remove("portal-swipe-drawer-dragging");
};

const bindSwipeDismiss = (el, { placement, onClose }) => {
  if (!el) return () => {};

  const state = {
    startX: 0,
    startY: 0,
    active: false,
    lockAxis: null,
    pointerId: null,
  };

  const begin = (clientX, clientY, pointerId = null) => {
    state.startX = clientX;
    state.startY = clientY;
    state.active = true;
    state.lockAxis = null;
    state.pointerId = pointerId;
  };

  const move = (clientX, clientY, event) => {
    if (!state.active) return;
    const dx = clientX - state.startX;
    const dy = clientY - state.startY;

    if (!state.lockAxis) {
      if (Math.abs(dx) < SWIPE_AXIS_LOCK_PX && Math.abs(dy) < SWIPE_AXIS_LOCK_PX) return;
      state.lockAxis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
    }
    if (state.lockAxis !== "x" || placement !== "right" || dx <= 0) return;

    const wrapper = getContentWrapper(el);
    if (wrapper) {
      wrapper.style.transform = `translateX(${dx}px)`;
      wrapper.classList.add("portal-swipe-drawer-dragging");
    }
    if (dx > 10 && event?.cancelable) event.preventDefault();
  };

  const end = (clientX, clientY, event) => {
    if (!state.active) return;
    const wrapper = getContentWrapper(el);
    resetWrapperTransform(wrapper);

    const dx = clientX - state.startX;
    const dy = clientY - state.startY;
    const shouldClose =
      state.lockAxis === "x" &&
      placement === "right" &&
      dx >= SWIPE_CLOSE_MIN_PX &&
      dx > 0 &&
      Math.abs(dx) > Math.abs(dy) * SWIPE_DOMINANCE_RATIO;

    state.active = false;
    state.lockAxis = null;
    state.pointerId = null;

    if (shouldClose && typeof onClose === "function") {
      onClose(event);
    }
  };

  const onTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    begin(touch.clientX, touch.clientY);
  };

  const onTouchMove = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    move(touch.clientX, touch.clientY, event);
  };

  const onTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    end(touch.clientX, touch.clientY, event);
  };

  const onPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    begin(event.clientX, event.clientY, event.pointerId);
    try {
      el.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  const onPointerMove = (event) => {
    if (state.pointerId !== null && event.pointerId !== state.pointerId) return;
    move(event.clientX, event.clientY, event);
  };

  const onPointerUp = (event) => {
    if (state.pointerId !== null && event.pointerId !== state.pointerId) return;
    end(event.clientX, event.clientY, event);
    try {
      el.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  el.addEventListener("touchstart", onTouchStart, { passive: true });
  el.addEventListener("touchmove", onTouchMove, { passive: false });
  el.addEventListener("touchend", onTouchEnd, { passive: true });
  el.addEventListener("touchcancel", onTouchEnd, { passive: true });
  el.addEventListener("pointerdown", onPointerDown);
  el.addEventListener("pointermove", onPointerMove);
  el.addEventListener("pointerup", onPointerUp);
  el.addEventListener("pointercancel", onPointerUp);

  return () => {
    el.removeEventListener("touchstart", onTouchStart);
    el.removeEventListener("touchmove", onTouchMove);
    el.removeEventListener("touchend", onTouchEnd);
    el.removeEventListener("touchcancel", onTouchEnd);
    el.removeEventListener("pointerdown", onPointerDown);
    el.removeEventListener("pointermove", onPointerMove);
    el.removeEventListener("pointerup", onPointerUp);
    el.removeEventListener("pointercancel", onPointerUp);
    resetWrapperTransform(getContentWrapper(el));
  };
};

const findDrawerRoot = (swipeKey) =>
  document.querySelector(`.portal-swipe-drawer--${swipeKey}.ant-drawer-open`);

/**
 * Right-side Drawer — mobile swipe left-to-right to close.
 * Swipe on title bar, left edge strip, or anywhere on the panel (horizontal drag).
 */
const SwipeableDrawer = ({
  open,
  onClose,
  placement = "right",
  swipeDismiss = true,
  swipeKey,
  className = "",
  rootClassName = "",
  children,
  afterOpenChange,
  ...drawerProps
}) => {
  const cleanupsRef = useRef([]);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const swipeClass = swipeKey ? `portal-swipe-drawer--${swipeKey}` : "";
  const mergedRootClassName = ["portal-swipe-drawer", swipeClass, className, rootClassName]
    .filter(Boolean)
    .join(" ");

  const clearBindings = useCallback(() => {
    cleanupsRef.current.forEach((cleanup) => cleanup());
    cleanupsRef.current = [];
  }, []);

  const bindSwipeTargets = useCallback(() => {
    if (!swipeKey || !swipeDismiss || placement !== "right" || !isSwipeEnvironment()) {
      return;
    }

    const drawerRoot = findDrawerRoot(swipeKey);
    if (!drawerRoot) return;

    const targets = new Set();
    const header = drawerRoot.querySelector(".ant-drawer-header");
    const edge = drawerRoot.querySelector(".portal-drawer-swipe-edge");
    const wrapper = drawerRoot.querySelector(".ant-drawer-content-wrapper");

    if (header) targets.add(header);
    if (edge) targets.add(edge);
    if (wrapper) targets.add(wrapper);

    clearBindings();
    targets.forEach((target) => {
      cleanupsRef.current.push(
        bindSwipeDismiss(target, {
          placement,
          onClose: (event) => onCloseRef.current?.(event),
        })
      );
    });
  }, [swipeKey, swipeDismiss, placement, clearBindings]);

  useEffect(() => {
    if (!open) {
      clearBindings();
      return undefined;
    }

    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 12;

    const tryBind = () => {
      if (cancelled) return;
      bindSwipeTargets();
      if (cleanupsRef.current.length === 0 && attempt < maxAttempts) {
        attempt += 1;
        window.setTimeout(tryBind, 50);
      }
    };

    tryBind();

    return () => {
      cancelled = true;
      clearBindings();
    };
  }, [open, bindSwipeTargets, clearBindings]);

  const handleAfterOpenChange = (visible) => {
    if (visible) {
      window.setTimeout(bindSwipeTargets, 0);
    } else {
      clearBindings();
    }
    afterOpenChange?.(visible);
  };

  const showSwipeEdge = swipeDismiss && isSwipeEnvironment() && placement === "right";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement={placement}
      rootClassName={mergedRootClassName}
      afterOpenChange={handleAfterOpenChange}
      {...drawerProps}
    >
      {showSwipeEdge ? (
        <div className="portal-drawer-swipe-edge" aria-hidden="true" />
      ) : null}
      {children}
    </Drawer>
  );
};

export default SwipeableDrawer;
